import { db } from "@/repositories/database";
import type { DRBackup } from "@/types";
import type { RepositoryBaseInterface } from "@/repositories/repository.interface";
import { BaseRepository } from "@/repositories/base.repository";
import { Logger } from "@/logger";
import { localDrawingRepository } from "./local-drawing.repository";

export const BACKUP_MAX_AGE_MS = 60 * 24 * 60 * 60 * 1000; // 60 jours ≈ 2 mois

class BackupRepository
  extends BaseRepository<DRBackup, number>
  implements RepositoryBaseInterface<DRBackup>
{
  private currentUserId: number | null = null;

  constructor() {
    super(db.backups, "backup");
  }

  async create(backupData: Omit<DRBackup, "id" | "userId">) {
    if (!this.currentUserId) {
      console.error("[BackupRepository] Current user ID is not set");
      throw new Error("Current user ID is not set");
    }
    return await db.backups.add({
      ...backupData,
      userId: this.currentUserId,
    } as DRBackup); // let Dexie create an id automatically
  }

  async createFromDrawingId(
    drawingId: number,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) {
    const { onSuccess, onError } = options || {};

    // Validate drawingId: must be a positive integer within safe integer range
    if (
      !Number.isInteger(drawingId) ||
      drawingId <= 0 ||
      drawingId > Number.MAX_SAFE_INTEGER
    ) {
      console.warn("[BackupRepository] Invalid drawingId:", drawingId);
      if (options?.onError) {
        options.onError(new Error(`Invalid drawing ID: ${drawingId}`));
      }
      return;
    }
    const drawing = await localDrawingRepository.getByIdOrThrow(drawingId);
    const binary = drawing.lastRevision.dwgDraft || drawing.lastRevision.dwg;
    const revisionId = drawing.lastRevision.id;

    if (!binary || !revisionId) {
      throw new Error("No binary data or revision ID found for backup");
    }

    return await this.create({
      drawingId,
      revisionId,
      vsf: undefined,
      dwg: binary,
      createdAt: new Date(),
    })
      .then((res) => {
        localDrawingRepository.resetSavesCount(drawingId);
        if (onSuccess) onSuccess();
        return res;
      })
      .catch((error) => {
        if (onError) onError(error);
        throw error;
      });
  }

  setCurrentUserId(userId: number) {
    this.currentUserId = userId;
  }

  fetchAllByNewest(drawingId: number) {
    if (!this.currentUserId) {
      return [];
    }
    const backups = db.backups
      .where("userId")
      .equals(this.currentUserId)
      .and((backup) => backup.drawingId === drawingId)
      .sortBy("createdAt");

    return backups;
  }

  deleteById(id: number) {
    return db.backups.delete(id);
  }

  deleteManyById(ids: number[]) {
    return db.backups.bulkDelete(ids);
  }

  deleteAll() {
    return db.backups.clear();
  }

  /**
   * Supprime les backups de dessins qui viennent d'être retirés du stockage local
   * (cf. `useLocalDrawingsRetention`) : sans copie locale du dessin, ces snapshots
   * DWG ne sont plus restaurables par l'utilisateur. En pratique presque toujours
   * vide — un dessin inactif depuis 2 mois a déjà vu ses backups purgés par
   * `pruneOlderThan` — mais garantit qu'aucun binaire orphelin ne subsiste.
   */
  async deleteByDrawingIds(drawingIds: number[]): Promise<number> {
    if (!drawingIds.length) return 0;
    return db.backups.where("drawingId").anyOf(drawingIds).delete();
  }

  /**
   * Prune backups older than maxAgeMs (retention policy). `createdAt` isn't an
   * indexed field on this table, so this scans in memory via `.filter()` rather
   * than `.where()` — avoids a Dexie schema migration for what stays a small table.
   */
  async pruneOlderThan(maxAgeMs: number): Promise<number> {
    const cutoff = Date.now() - maxAgeMs;
    const count = await db.backups
      .filter((backup) => backup.createdAt.getTime() < cutoff)
      .delete();
    if (count > 0) {
      Logger.warn(
        `[BackupRepository] Pruned ${count} backup(s) older than ${maxAgeMs}ms`
      );
    }
    return count;
  }
}

export const backupRepository = new BackupRepository();
